/**
 * Alert <uo-accordion>
 *
 * Attributes:
 */

// Import legacy code
import './legacy';

// Import children web component
import './item';
import './item-li';

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property, queryAssignedElements, state } from 'lit/decorators.js';
import { isDefined, isEmpty } from '@automator/utilities';
import Fuse from 'fuse.js';

// Set Fuse settings as a constant outside of the class
// We don't want to create a property with it for every instance of the web component
const fuseSettings = {
	// keys: List of keys that will be searched. This supports nested paths, weighted search, searching in arrays of strings and objects.
	keys: [
		{ name: 'title', weight: 4 },
		{ name: 'keywords', weight: 8 }
	],

	// minMatchCharLength: Only the matches whose length exceeds this value will be returned.
	minMatchCharLength: 1,

	// threshold: At what point does the match algorithm give up. A threshold of 0.0 requires a perfect match (of both letters and location), a threshold of 1.0 would match anything.
	threshold: 0.5,

	// Whether the matches should be included in the result set. When true, each record in the result set will include the indices of the matched characters.
	includeMatches: true
}

// Import style for web component
import styles from './web-component-style.scss';

// eslint-disable-next-line require-jsdoc
@customElement( 'uo-accordion' )
export class AutomatorAccordion extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	@property( { type: Boolean, attribute: 'list-search' } )
		accessor enableSearch = false;

	@property( { type: String, attribute: 'search-placeholder' } )
		accessor searchPlaceholder = 'Search';

	@property( { type: String, attribute: 'no-results-text' } )
		accessor noResultsText = 'No results found';

	@state()
		accessor _hasResults = true;

	// Items
	@queryAssignedElements( { selector: 'uo-accordion-item' } ) accessor $items;

	// eslint-disable-next-line require-jsdoc
	constructor() {
		super();
	}

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div class="wrapper">
				${ this._searchTemplate }

				<div 
					class="accordion"
					?list-search=${ this.enableSearch }
				>
					${ this._noResultsTemplate }

					<slot></slot>
				</div>
			</div>
		`;
	}

	/**
	 * Returns the search section of the accordion
	 *
	 * @return {TemplateResult} The TemplateResult of the accordion search section
	 */
	get _searchTemplate() {
		// Check if there is a heading defined
		if ( ! this.enableSearch ) {
			return;
		}

		return html`
			<div class="search">
				<uo-icon id="search"></uo-icon>

				<uo-text-field
					auto-focus
					placeholder="${ this.searchPlaceholder }"
					@change=${ ( event ) => {
					   this.search( event.target.value );
					} }
				></uo-text-field>
			</div>
		`;
	}

	/**
	 * Returns the "No results" section
	 *
	 * @return {TemplateResult} The TemplateResult of the "No results"
	 */
	get _noResultsTemplate() {
		// Check if there is a heading defined
		if ( ! this.enableSearch ) {
			return;
		}

		// Check if it currently has results
		if ( this._hasResults ) {
			return;
		}

		return html`
			<div class="no-results">
				<div class="no-results-heading">
					${ this.noResultsText }
				</div>
				<div class="no-results-description">Try searching again</div>
			</div>
		`;
	}

	/**
	 * Performs a search and shows the results
	 *
	 * @param  {String} query The term to be searched
	 * @return {undefined}    The results
	 */
	search( query = '' ) {

		// Check if the user is searching something
		if ( isEmpty( query ) ) {

			// The user is not searching, reset results
			this._resetSearchResults();

		} else {

			// Get the results
			const results = this._fuse.search( query );

			// Check if it has results
			this._hasResults = ! isEmpty( results );

			// Check if it has results
			if ( this._hasResults ) {

				// Show results
				this._showResults( results );

			} else {

				// Hide all groups
				this._hideAllGroups();

			}

		}

	}

	/**
	 * Resets the search results to the initial state
	 * In practice, this:
	 * 1. Closes all accordions
	 * 2. Shows all hidden elements
	 *
	 * @return {undefined}
	 */
	_resetSearchResults() {

		this._hasResults = true;

		// Do for all accordions and items
		this._applyToAll(

			// Callback for the accordions
			( $accordion ) => {
				// Show it
				if ( $accordion.classList.contains( 'uap-hidden' ) ) {
					$accordion.classList.remove( 'uap-hidden' );
				}

				// Close it
				$accordion.open = false;
			},

			// Callback for the items
			( $item ) => {
				// Show it
				if ( $item.classList.contains( 'uap-hidden' ) ) {
					$item.classList.remove( 'uap-hidden' );
				}
			},

		);

	}

	/**
	 * Hides all groups. This is used when there aren't results.
	 * Items are not hidden
	 *
	 * @return {undefined}
	 */
	_hideAllGroups() {

		// Do for all accordions
		this._applyToAll(

			// Callback for the accordions
			( $accordion ) => {
				// Hide it
				if ( ! $accordion.classList.contains( 'uap-hidden' ) ) {
					$accordion.classList.add( 'uap-hidden' );
				}
			},

		);

	}

	/**
	 * Shows results. We can assume the array will contain results, as
	 * we handle the "No results" scenario in a different method
	 *
	 * @param {Array} results The Fuse output
	 * @return {undefined}
	 */
	_showResults( results ) {

		// Hide all groups
		this._hideAllGroups();

		// Iterate the results
		results.forEach( ( result ) => {

			const $accordion = result.item.$accordion;
			const $item = result.item.$item;

			// Show and open the group
			// This will be done only once, even if there are multiple results
			// from the same group
			if ( $accordion.classList.contains( 'uap-hidden' ) ) {
				// Show it
				$accordion.classList.remove( 'uap-hidden' );

				// Open it
				$accordion.open = true;

				// Hide all the children, we're going to show only the results
				$accordion.$items.forEach( ( $childItem ) => {
					// Hide it
					if ( ! $childItem.classList.contains( 'uap-hidden' ) ) {
						$childItem.classList.add( 'uap-hidden' );
					}
				} );
			}

			// Show the result
			if ( $item.classList.contains( 'uap-hidden' ) ) {
				$item.classList.remove( 'uap-hidden' );
			}

		} );

	}

	/**
	 * Invokes callbacks that run for all accordions, and for all their children
	 *
	 * @param  {Callback} callbackAccordions Callback that runs for accordions
	 * @param  {Callback} callbackItems      Callback that runs for items inside accordions
	 * @return {undefined}
	 */
	_applyToAll( callbackAccordions, callbackItems ) {
		// Check if at least one callback is defined
		if ( ! isDefined( callbackAccordions ) && ! isDefined( callbackItems ) ) {
			return;
		}

		// Iterate groups
		this.$items.forEach( ( $group ) => {
			// Check if the callback for the accordions is defined
			if ( isDefined( callbackAccordions ) ) {
				// Invoke callback for the accordions
				callbackAccordions( $group );
			}

			// Check if the callback for the items is defined
			if ( isDefined( callbackItems ) ) {
				// Iterate the items
				$group.$items.forEach( ( $item ) => {
					// Invoke callback for the items
					callbackItems( $item );
				} );
			}
		} );
	}

	/**
	 * Returns a JSON map of the content of the accordion
	 * We will use this to perform searches
	 *
	 * @return {Array} Array with data about each item
	 */
	get _searchMap() {
		// Check if there isn't a search map defined
		if ( ! isDefined( this._internalSearchMap ) ) {
			// Create it
			this._updateSearchMap();
		}

		// Return it
		return this._internalSearchMap;
	}

	/**
	 * Sets the search map
	 *
	 * @param  {Array} data The search map data
	 * @return {undefined}
	 */
	set _searchMap( data = [] ) {
		this._internalSearchMap = data;
	}

	/**
	 * Creates and saves a search map with data about each item
	 *
	 * @return {undefined}
	 */
	_updateSearchMap() {
		let searchMap = [];

		// Iterate items
		this.$items.forEach( ( $item ) => {
			// Update the search map of the items
			$item.updateSearchMap();

			// Add search data
			searchMap = [ ...searchMap, ...$item._searchMap ];
		} );

		this._searchMap = searchMap;
	}

	/**
	 * Returns the Fuse instance used for searching items
	 *
	 * @return {Fuse} The Fuse instance
	 */
	get _fuse() {
		// Check if we already created a Fuse instance
		if ( isDefined( this._internalFuse ) ) {
			return this._internalFuse;
		}

		// Create it
		this._internalFuse = new Fuse(
			// The array with the data
			this._searchMap,

			// The settings
			fuseSettings
		);

		return this._internalFuse;
	}
}
